<template>
  <v-card 
    class="mx-auto"
  > 
    
    <v-dialog
      v-model="showAssignLicenseAllocation"
      max-width="90%"
      v-if="this.selectedLicense"
    >
      <videon-card heading="License Allocations" showClose="true" @close="showAssignLicenseAllocation = false">
        <v-card-text>
          <license-allocations :license="this.selectedLicense" @close="loadLicenses(0)" />
        </v-card-text>
      </videon-card>
    </v-dialog>
    
    
    <v-toolbar flat>
      <v-toolbar-title>
        <v-avatar
          height="32px"
          class="mx-auto"
        >
          <videon-logo />
        </v-avatar>
        License Manager
      </v-toolbar-title>
      <v-spacer></v-spacer>
    </v-toolbar>
    
    <v-divider/>
    <search-filter v-model="searchFilters" :filters="filterTypes" />
    <v-divider/>
    
    <v-card-text>
      <v-card flat>
        <v-card-title>
          Licenses assigned to {{ orgName }}
          <v-spacer/>
        </v-card-title>
        
        <v-card-text>
          <v-alert dense outlined type="error" v-if="error" >
            {{ errorText }}
          </v-alert>
          
          <v-row>
            <v-col cols="12">
              <v-data-table
                :headers="headers"
                :items="items"
                item-key="license_id"
                :expanded.sync="expanded"
                :loading="loading"
                loadingText="Loading licenses..."
                no-data-text="No licenses"
                :page.sync="page"
                :items-per-page="itemsPerPage"
                hide-default-footer
                @click:row="(item, slot) => slot.expand(!slot.isExpanded)"
              >
                <template v-slot:item.license_name="{ item }">
                  <v-row align="center">
                    <v-icon v-if="item.org_guid">location_city</v-icon>
                    <v-icon v-else-if="item.serial_number">storage</v-icon>
                    <span v-if="item.license_name" class="subtitle-1" >{{ item.license_name }}</span>
                    <span v-else class="subtitle-1 text--disabled" >Unnamed</span>
                  </v-row>
                </template>
                <template v-slot:item.profile_guid="{ item }">
                  <span class="">{{ entitlementProfileName(item) }}</span>
                </template>
                <template v-slot:item.unlimited="{ item }">
                  {{ licenseTypeLabel(item) }}
                </template>
                <template v-slot:item.profile_platform="{ item }">
                  {{ $helpers.getLabel(item.profile_platform) }}
                </template>
                <template v-slot:item.created="{ item }">
                  {{ item.created | moment("YYYY/MM/DD") }}
                </template>
                <template v-slot:item.expires="{ item }">
                  <span v-if="item.expires" :class="hasExpired(item) ? 'red--text' : ''">
                    {{ item.expires | moment("YYYY/MM/DD") }}
                  </span>
                  <span v-else>Never</span>
                </template>
                <template v-slot:item.actions="{ item }">
                  <v-btn
                    class="ma-1"
                    x-small
                    outlined
                    color="primary"
                    @click="assignLicenses(item)"
                    v-if="canEdit && item.allocation_limit && item.allocation_limit > 0"
                  >
                    Manage Allocation
                  </v-btn>
                </template>
                <template v-slot:expanded-item="{ headers, item }">
                  <td :colspan="headers.length" class="pt-4 pb-3">
                    <v-row class="mb-1" no-gutters>
                      <v-col cols="4">
                        <license-label :license="item"></license-label>
                      </v-col>
                      <v-col cols="5">
                        <strong>License Type:</strong> {{ licenseTypeLabel(item) }}
                      </v-col>
                      <v-col cols="3" class="text-right">
                        <strong>Platform:</strong> {{ $helpers.getLabel(item.profile_platform) }}
                      </v-col>
                    </v-row>
                    
                    <v-row v-if="item.serial_number" class="mb-1" no-gutters>
                      <v-col cols="12">
                        <strong>Device Serial:</strong> {{ item.serial_number }}
                      </v-col>
                    </v-row>
                    
                    <v-row v-if="item.sales_order" class="mb-1" no-gutters>
                      <v-col cols="12">
                        <strong>Sales Order:</strong> {{ item.sales_order }}
                      </v-col>
                    </v-row>
                    
                    <v-row class="mb-1" no-gutters>
                      <v-col cols="12">
                        <strong>Issue Date:</strong>&nbsp; 
                        <span>{{ item.created | moment("YYYY/MM/DD") }}</span>
                      </v-col>
                    </v-row>
                    
                    <v-row class="mb-1" no-gutters>
                      <v-col cols="12" :class="(hasExpired(item)) ? 'red--text' : ''">
                        <strong>Expiration Date:</strong>&nbsp;
                        <span v-if="item.expires">{{ item.expires | moment("YYYY/MM/DD") }} ({{ expirationDays(item.expires) }})</span>
                        <span v-else>Never</span>
                      </v-col>
                    </v-row>
                    
                    <div class="text-right">
                      <v-btn
                        v-if="item.allocation_limit"
                        class="ma-1"
                        small
                        outlined
                        color="primary"
                        @click="manageAllocation(item)"
                      >
                        Manage Allocation
                      </v-btn>
                    </div>
                  </td>
                </template>
              </v-data-table>
              <data-table-pagination v-model="page" :paginationTokens="paginationTokens" />
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-card-text>
  </v-card>
</template>

<script> 
  import { mapGetters } from 'vuex'
  import LicenseAllocations from './LicenseAllocations.vue'
  
  import moment from 'moment'
  
  export default {
    name: 'OrgLicenseManager',
    components: {
      LicenseAllocations,
    },
    data() {
      return { 
        error: false,
        errorText: '',
        
        loading: false,
        
        showAssignLicenseAllocation: false,
        selectedLicense: false,
        
        filterTypes: [
          'license_name', 
          'sales_order', 
          'profile_platform', 
          'serial_number', 
          'created_start',
          'created_end',
          'expires_start',
          'expires_end'
        ],
        
        searchFilters: {},
        
        items: [],
        expanded: [],
        
        
        itemsPerPage: 50,
        page: 1,
        pageCount: 0,
      
        paginationTokens: []
      }
    },
    mounted() {
      this.loadLicenses(0)
    },
    watch: {
      page() {
        this.loadLicenses(this.page - 1)
      },
      itemsPerPage() {
        this.items = []
        this.page = 1
        this.loadLicenses(this.page - 1)
      },
      searchFilters() {
        console.log('OrgLicenseManager searchFilters updated', this.searchFilters)
        this.loadLicenses(0)
      }
    },
    computed: {
      ...mapGetters('user', ['organization']),
      
      orgName() {
        if (this.organization.org_name) {
          return this.organization.org_name
        }
        return 'Unknown Org'
      },
      canEdit() {
        if (this.organization) {
          return this.$organizations.getOrg(this.organization.org_guid).canEdit()
        }
        return false
      },
      headers() {
        var headers = []
        
        headers.push({ text: 'License Name', align: '', value: 'license_name', sortable: true })
        headers.push({ text: 'Sales Order', align: '', value: 'sales_order', sortable: true })
        
        headers.push({ text: 'Entitlement', align: '', value: 'profile_guid', sortable: true })
        
        headers.push({ text: 'Issue Date', align: '', value: 'created', sortable: true })
        headers.push({ text: 'Expiration Date', align: 'center', value: 'expires', sortable: true })
        
        return headers
      },
    },
    methods: {
      licenseTypeLabel(item) {
        if (item.unlimited) {
          return 'Unlimited License'
        } else if (item.allocation_limit) {
          return 'Per-Device License (' + item.allocation_limit + ' allocated, ' + item.allocated + ' in use)'
        } else if (item.serial_number) {
          return 'Base License'
        }
        return 'Unknown'
      },
      
      entitlementProfileName(item) {
        if (item.profile_guid) {
           return this.$entitlementProfiles.getEntitlementProfile(item.profile_guid).displayName()
        }
        return 'Profile GUID Missing'
      },
      
      hasExpired(item) {
        var expirationDate = moment(item.expires)
        var todaysdate = moment()
        
        var expirationDays = expirationDate.diff(todaysdate, 'days')
        
        if (expirationDays < 0) {
          return true
        }
        
        return false
      },
      
      expirationDays(expiration) {
        var expirationDate = moment(expiration)
        var todaysdate = moment()
        var expirationDays = expirationDate.diff(todaysdate, 'days')
        
        if (expirationDays < 0) {
          return Math.abs(expirationDays) + ' days ago'
        }
        
        return expirationDays + ' days'
      },
      
      loadLicenses(paginationIndex = 0) {
        console.log('OrgLicenseManager loadLicenses')
        
        this.showAssignLicenseAllocation = false
        this.loading = true
        this.items = []
        
        const paginationToken = this.paginationTokens[paginationIndex - 1]
        
        var queryParams = {
          'pagination_size': this.itemsPerPage,
          'org_guid': this.organization.org_guid
        }
        
        if (paginationToken) {
          queryParams.pagination_token = paginationToken
        }
        
        var filterKeys = Object.keys(this.searchFilters)
        if (filterKeys.length > 0) {
          queryParams = Object.assign(queryParams, this.searchFilters)
          queryParams.show_serials = true
        } else {
          // default show all org & serial licenses...
          queryParams.show_orgs = true
          queryParams.show_serials = true
        }
        
        this.axios.get('/entitlements/licenses', {'params': queryParams}).then((response) => {
          console.log('OrgLicenseManager loadLicenses response', response)
          
          if (response.data.licenses.length > 0) {
            this.items = response.data.licenses
            
            if (response.data.pagination_token) {
              this.$set(this.paginationTokens, paginationIndex, response.data.pagination_token)
            }
          }
          
          this.loading = false
        }).catch((error) => {
          console.log('OrgLicenseManager loadLicenses error', error)
          this.error = true
          this.errorText = this.$helpers.parseError(error)
          this.loading = false
        })
      },
      
      manageAllocation(item) {
        this.selectedLicense = item
        this.allocationLoading = true
        this.showAssignLicenseAllocation = true
      },
      
      closeManageAllocation() {
        this.showAssignLicenseAllocation = false
        this.loadLicenses(0)
      }
    }
  }
</script>